// Birth details that travel with a signup. The guest chart lives in one
// browser's localStorage, so a signup started in an in-app browser
// (Instagram, iMessage) and confirmed in Safari would reach an account with
// no chart. The signup also stores the birth details in the auth record's
// user metadata (The Web's own Supabase, same project as the star_chart row),
// and the first sign-in builds the chart from them when this browser has no
// guest chart. No imports, so scripts/review/ob007-unit.mjs runs it under plain Node.

export const SIGNUP_BIRTH_KEY = "birth_data";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v) => (typeof v === "string" && v.trim() ? v.slice(0, 200) : null);

// Keep only the fields a chart needs, and drop anything malformed. Returns
// null when there's no usable chart in it. The same rules check what goes
// out with the signup and what comes back on sign-in.
export function cleanBirthData(bd) {
  if (!bd || typeof bd !== "object") return null;
  const d = bd.birthDate || {};
  const year = num(d.year), month = num(d.month), day = num(d.day);
  if (year === null || month === null || day === null) return null;
  if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  // A day the month doesn't have (Feb 31) rolls over in Date.UTC.
  if (new Date(Date.UTC(year, month - 1, day)).getUTCDate() !== day) return null;

  const loc = bd.birthLocation || {};
  const lat = num(loc.lat), lng = num(loc.lng);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const birthLocation = { city: str(loc.city), country: str(loc.country), lat, lng, timezone: str(loc.timezone) };

  let birthTime = null;
  const t = bd.birthTime;
  if (bd.hasBirthTime !== false && t && typeof t === "object") {
    const hour = num(t.hour), minute = num(t.minute);
    if (hour !== null && minute !== null && hour >= 1 && hour <= 12 && minute >= 0 && minute < 60
      && (t.period === "AM" || t.period === "PM")) {
      birthTime = { hour, minute, period: t.period };
    }
  }

  return { birthDate: { year, month, day }, hasBirthTime: !!birthTime, birthTime, birthLocation };
}

/** The user metadata a signup carries, or {} when there's no chart yet. */
export function signupBirthMetadata(birthData) {
  const clean = cleanBirthData(birthData);
  return clean ? { [SIGNUP_BIRTH_KEY]: clean } : {};
}

/** Birth details from a signed-in user's metadata, or null. */
export function birthDataFromMetadata(userMetadata) {
  return cleanBirthData(userMetadata?.[SIGNUP_BIRTH_KEY]);
}

/** Which birth details become a new account's chart: this browser's guest
 *  chart wins; the signup's copy covers a different browser. */
export function chartBirthDataFor({ guest, userMetadata }) {
  return guest || birthDataFromMetadata(userMetadata);
}

/** The birth details to save as the account's chart on sign-in, or null.
 *  Never overwrites a saved chart. */
export function firstSignInChart({ guest, userMetadata, hasChart }) {
  return hasChart ? null : chartBirthDataFor({ guest, userMetadata });
}
